import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Play, Pause, Download, X, Music, Video, Gift, Heart, ExternalLink, Volume2, VolumeX
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Landing page content (blurred behind the player)
const BlurredLandingBackground = () => (
  <div className="fixed inset-0 overflow-hidden">
    {/* Animated background similar to landing page */}
    <div className="absolute inset-0 bg-gradient-to-br from-purple-900/90 via-black to-indigo-900/90" />
    <div className="absolute inset-0 opacity-30">
      {[...Array(50)].map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-white animate-pulse"
          style={{
            width: Math.random() * 3 + 1 + 'px',
            height: Math.random() * 3 + 1 + 'px',
            left: Math.random() * 100 + '%',
            top: Math.random() * 100 + '%',
            animationDelay: Math.random() * 3 + 's',
            animationDuration: Math.random() * 3 + 2 + 's'
          }}
        />
      ))}
    </div>
    {/* Blur overlay */}
    <div className="absolute inset-0 backdrop-blur-md bg-black/40" />
  </div>
);

export default function PublicPlayer() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  
  // Hide download when arriving from social share
  const searchParams = new URLSearchParams(location.search);
  const isShareView = searchParams.get('ref') === 'share';
  
  // Determine if this is audio or video content
  const isVideo = type === 'video' || type === 'slideshow' || type === 'birthday_video';
  const mediaRef = isVideo ? videoRef : audioRef;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        let endpoint = '';
        
        if (type === 'gift') {
          endpoint = `/api/gifts/${id}/public`;
        } else if (type === 'song') {
          endpoint = `/api/songs/${id}/public`;
        } else if (type === 'video' || type === 'slideshow' || type === 'birthday_video') {
          endpoint = `/api/videos/${id}/public`;
        } else {
          throw new Error('Invalid content type');
        }
        
        const response = await fetch(`${API}${endpoint}`);
        if (!response.ok) {
          throw new Error('Content not found');
        }
        
        const result = await response.json();
        setData(result);
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [type, id]);

  const handlePlayPause = () => {
    const media = mediaRef.current;
    if (!media) return;
    
    if (isPlaying) {
      media.pause();
    } else {
      media.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    const media = mediaRef.current;
    if (media) {
      setProgress((media.currentTime / media.duration) * 100 || 0);
    }
  };

  const handleLoadedMetadata = () => {
    const media = mediaRef.current;
    if (media) {
      setDuration(media.duration);
    }
  };

  const handleSeek = (e) => {
    const media = mediaRef.current;
    if (!media) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    media.currentTime = percent * media.duration;
  };

  const handleDownload = () => {
    let downloadUrl = '';
    let filename = 'download';
    
    if (type === 'gift' || type === 'song') {
      // For songs/gifts, use the download-with-cover endpoint if available
      const songId = data?.song?.song_id || data?.song_id || id;
      downloadUrl = `${API}/api/songs/${songId}/download-with-cover`;
      filename = `${data?.song?.title || data?.title || 'song'}.mp3`;
    } else {
      // For videos, use the video download endpoint
      downloadUrl = `${API}/api/videos/${id}/download`;
      filename = `${data?.title || 'video'}.mp4`;
    }
    
    // Open download in new tab
    window.open(downloadUrl, '_blank');
    toast.success('Download started!');
  };

  const handleClose = () => {
    // Stop media
    if (mediaRef.current) {
      mediaRef.current.pause();
    }
    // Navigate to landing page
    navigate('/');
  };

  // Esc key closes the fullscreen player (consistent with other players)
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get display data based on type
  const getDisplayData = () => {
    if (!data) return { title: '', message: '', image: '', mediaUrl: '' };
    
    if (type === 'gift') {
      return {
        title: data.gift?.recipient_name ? `For ${data.gift.recipient_name}` : 'A Musical Gift',
        subtitle: data.song?.title || 'Special Song',
        message: data.gift?.message || '',
        image: data.cover?.image_url || data.song?.image_url || '',
        mediaUrl: data.song?.audio_url || '',
        from: data.gift?.sender_name
      };
    } else if (type === 'song') {
      return {
        title: data.title || 'Untitled Song',
        subtitle: '',
        message: '',
        image: data.image_url || '',
        mediaUrl: data.audio_url || ''
      };
    } else {
      return {
        title: data.title || data.name || 'Video',
        subtitle: data.video_type || '',
        message: '',
        image: data.thumbnail_url || '',
        mediaUrl: data.video_url || ''
      };
    }
  };

  const displayData = getDisplayData();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <BlurredLandingBackground />
        <div className="relative z-10 text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <BlurredLandingBackground />
        <div className="relative z-10 text-center">
          <p className="text-white text-xl mb-4">{error}</p>
          <Button onClick={() => navigate('/')} className="btn-primary">
            Go to Wrapped In Music
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <BlurredLandingBackground />
      
      {/* Player Card */}
      <div className="relative z-10 w-full max-w-lg">
        {/* Close button — standardized fullscreen-player exit */}
        <button
          onClick={handleClose}
          aria-label="Close player"
          title="Close (Esc)"
          data-testid="public-player-close-btn"
          className="absolute -top-3 -right-3 z-[60] p-2 rounded-full bg-black/70 hover:bg-black/90 text-white backdrop-blur-sm border border-white/10 transition-colors shadow-lg flex items-center gap-2"
        >
          <X className="h-5 w-5" />
        </button>
        
        <div className="bg-zinc-900/90 backdrop-blur-xl rounded-2xl overflow-hidden shadow-2xl border border-white/10">
          {/* Cover Art / Video */}
          <div className="relative bg-zinc-800" style={{ aspectRatio: isVideo ? '16/9' : '1/1' }}>
            {isVideo && displayData.mediaUrl ? (
              <video
                ref={videoRef}
                src={displayData.mediaUrl}
                className="w-full h-full object-contain bg-black"
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={() => setIsPlaying(false)}
                onClick={handlePlayPause}
                muted={isMuted}
                playsInline
              />
            ) : (
              <>
                {displayData.image ? (
                  <img 
                    src={displayData.image} 
                    alt={displayData.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-600 to-indigo-800">
                    {type === 'gift' ? (
                      <Gift className="h-24 w-24 text-white/50" />
                    ) : (
                      <Music className="h-24 w-24 text-white/50" />
                    )}
                  </div>
                )}
                {/* Hidden audio element */}
                <audio
                  ref={audioRef}
                  src={displayData.mediaUrl}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={() => setIsPlaying(false)}
                />
              </>
            )}
            
            {/* Play overlay for audio */}
            {!isVideo && (
              <div 
                className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer hover:bg-black/40 transition-colors"
                onClick={handlePlayPause}
              >
                <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  {isPlaying ? (
                    <Pause className="h-10 w-10 text-white" />
                  ) : (
                    <Play className="h-10 w-10 text-white ml-1" />
                  )}
                </div>
              </div>
            )}
            
            {/* Gift ribbon */}
            {type === 'gift' && (
              <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                <Gift className="h-4 w-4" />
                Gift
              </div>
            )}
          </div>
          
          {/* Info & Controls */}
          <div className="p-6">
            {/* Title & Message */}
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-white mb-1">{displayData.title}</h2>
              {displayData.subtitle && (
                <p className="text-primary text-sm">{displayData.subtitle}</p>
              )}
              {displayData.message && (
                <p className="text-zinc-400 text-sm mt-2 italic">"{displayData.message}"</p>
              )}
              {displayData.from && (
                <p className="text-zinc-500 text-sm mt-1">From: {displayData.from}</p>
              )}
            </div>
            
            {/* Progress Bar */}
            <div 
              className="h-1.5 bg-zinc-700 rounded-full cursor-pointer mb-4"
              onClick={handleSeek}
            >
              <div 
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            
            {/* Time */}
            <div className="flex justify-between text-xs text-zinc-500 mb-4">
              <span>{formatTime(mediaRef.current?.currentTime || 0)}</span>
              <span>{formatTime(duration)}</span>
            </div>
            
            {/* Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* Play/Pause */}
                <Button
                  onClick={handlePlayPause}
                  className="w-12 h-12 rounded-full bg-primary hover:bg-primary/80"
                >
                  {isPlaying ? (
                    <Pause className="h-5 w-5" />
                  ) : (
                    <Play className="h-5 w-5 ml-0.5" />
                  )}
                </Button>
                
                {/* Mute (for video) */}
                {isVideo && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsMuted(!isMuted)}
                    className="text-zinc-400 hover:text-white"
                  >
                    {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                  </Button>
                )}
              </div>
              
              {/* Download - hidden on social share views */}
              {!isShareView && (
                <Button
                  onClick={handleDownload}
                  className="bg-green-600 hover:bg-green-500 text-white"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              )}
            </div>
          </div>
          
          {/* CTA Footer */}
          <div className="px-6 py-4 bg-gradient-to-r from-purple-600/20 to-indigo-600/20 border-t border-white/10">
            <p className="text-zinc-400 text-sm text-center mb-3">
              Create your own AI-powered songs and gift cards!
            </p>
            <Button
              onClick={() => navigate('/')}
              className="w-full btn-primary"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Explore Wrapped In Music
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
